import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { PartnerCadence, fetchPartnerCadences } from '../api/client';

interface Props {
  open: boolean;
  onClose: () => void;
}

const PATTERN_COLORS: Record<string, string> = {
  month_end: '#d1fae5',
  fixed_20: '#fef3c7',
  fixed_25: '#fef3c7',
  fixed_26: '#fef3c7',
  fixed_27: '#fef3c7',
  fixed_19: '#fef3c7',
  fixed_5:  '#fef3c7',
  fixed_10: '#fef3c7',
  fixed_3:  '#fef3c7',
  fixed_4:  '#fef3c7',
  irregular_transactional: '#fce7f3',
  unknown: '#f4f4f5',
};

const PATTERN_LABEL: Record<string, string> = {
  month_end: '月末締',
  fixed_20: '20日発生 (顧問料/家賃系)',
  fixed_25: '25日発生',
  fixed_26: '26日発生',
  fixed_27: '27日発生 (保険/SaaS系)',
  fixed_19: '19日発生',
  fixed_5:  '5日発生',
  fixed_10: '10日発生',
  fixed_3:  '3日発生',
  fixed_4:  '4日発生',
  irregular_transactional: '取引単位 (購入日 = 発生日)',
  unknown: '未学習/irregular',
};

export const PartnerCadencePanel: React.FC<Props> = ({ open, onClose }) => {
  const [items, setItems] = useState<PartnerCadence[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<string>('all');
  const [minConf, setMinConf] = useState<number>(0.5);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchPartnerCadences(undefined, 0);
      setItems(r.items);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const filtered = useMemo(() => {
    return items
      .filter((i) => (filter === 'all' || i.issue_day_pattern === filter))
      .filter((i) => (i.confidence || 0) >= minConf);
  }, [items, filter, minConf]);

  const distribution = useMemo(() => {
    const c: Record<string, number> = {};
    for (const i of items) c[i.issue_day_pattern] = (c[i.issue_day_pattern] || 0) + 1;
    return c;
  }, [items]);

  if (!open) return null;

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000 }} />
      <div style={{
        position: 'fixed', top: '5%', left: '5%', right: '5%', bottom: '5%',
        background: '#fff', zIndex: 1001, borderRadius: '12px', padding: '20px',
        overflow: 'auto', boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
        display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem' }}>📅 業者別 発生日・期日 cadence</h2>
          <button onClick={onClose} style={{
            padding: '6px 16px', border: '1px solid #d5d3d0', borderRadius: '6px',
            background: '#fff', cursor: 'pointer', fontSize: '0.8rem',
          }}>閉じる</button>
        </div>
        <p style={{ fontSize: '0.75rem', color: '#555', marginTop: '6px' }}>
          freee 過去 6ヶ月の issue_date 分布から partner 毎の発生日パターンを自動学習。confidence ≥ 0.70 のみ automation に利用。
        </p>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', margin: '10px 0', flexWrap: 'wrap', flexShrink: 0 }}>
          <label style={{ fontSize: '0.75rem' }}>pattern:</label>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} style={{ fontSize: '0.75rem', padding: '4px' }}>
            <option value="all">全て ({items.length})</option>
            {Object.entries(distribution).sort((a, b) => b[1] - a[1]).map(([p, n]) => (
              <option key={p} value={p}>{PATTERN_LABEL[p] || p} ({n})</option>
            ))}
          </select>
          <label style={{ fontSize: '0.75rem' }}>min confidence: {minConf.toFixed(2)}</label>
          <input type="range" min="0" max="1" step="0.05" value={minConf} onChange={(e) => setMinConf(Number(e.target.value))} />
          <button onClick={refresh} disabled={loading} style={{
            padding: '4px 12px', fontSize: '0.75rem', background: '#2e7cf6', color: '#fff',
            border: 'none', borderRadius: '4px', cursor: 'pointer',
          }}>{loading ? '読込中' : '更新'}</button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
            <thead style={{ position: 'sticky', top: 0, background: '#fafafa' }}>
              <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '6px', textAlign: 'left' }}>pid</th>
                <th style={{ padding: '6px', textAlign: 'left' }}>partner</th>
                <th style={{ padding: '6px', textAlign: 'center' }}>pattern</th>
                <th style={{ padding: '6px', textAlign: 'center' }}>anchor日</th>
                <th style={{ padding: '6px', textAlign: 'center' }}>confidence</th>
                <th style={{ padding: '6px', textAlign: 'center' }}>n</th>
                <th style={{ padding: '6px', textAlign: 'left' }}>notes</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((i) => (
                <tr key={i.partner_id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '6px' }}>{i.partner_id}</td>
                  <td style={{ padding: '6px' }}>{i.partner_name || '—'}</td>
                  <td style={{ padding: '6px', textAlign: 'center' }}>
                    <span style={{
                      display: 'inline-block', padding: '2px 8px', borderRadius: '10px',
                      fontSize: '0.7rem', background: PATTERN_COLORS[i.issue_day_pattern] || '#f4f4f5',
                      fontWeight: 600,
                    }}>{PATTERN_LABEL[i.issue_day_pattern] || i.issue_day_pattern}</span>
                  </td>
                  <td style={{ padding: '6px', textAlign: 'center' }}>{i.issue_anchor_day ?? '—'}</td>
                  <td style={{ padding: '6px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
                      <div style={{ width: '60px', height: '6px', background: '#e5e7eb', borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{
                          width: `${(i.confidence || 0) * 100}%`, height: '100%',
                          background: (i.confidence || 0) >= 0.7 ? '#16a34a' : '#eab308',
                        }} />
                      </div>
                      <span style={{ fontSize: '0.7rem', color: '#555' }}>{(i.confidence || 0).toFixed(2)}</span>
                    </div>
                  </td>
                  <td style={{ padding: '6px', textAlign: 'center' }}>{i.sample_size}</td>
                  <td style={{ padding: '6px', fontSize: '0.7rem', color: '#555' }}>
                    {i.manual_override ? '⚑ manual' : ''}
                    {(i.confidence || 0) < 0.70 && '⚠ low confidence (automation 不使用)'}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} style={{ padding: '20px', textAlign: 'center', color: '#8a8580' }}>該当なし</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};
